// Coordinate PWA updates with the existing refresh prompt. A version check can
// become ready before the browser has installed the updated service worker.
let updateReadyRegistration = null;
let reloading = false;

const reloadOnce = () => {
  if (reloading) return;
  reloading = true;
  window.location.reload();
};

const markUpdateReady = (registration, onUpdateReady) => {
  updateReadyRegistration = registration;
  if (onUpdateReady) onUpdateReady(registration);
};

const trackInstalling = (worker, registration, onUpdateReady) => {
  if (!worker) return;
  worker.addEventListener('statechange', () => {
    if (worker.state === 'installed' && navigator.serviceWorker.controller) {
      markUpdateReady(registration, onUpdateReady);
    }
  });
};

// Registers the worker without interrupting an open form or payment flow.
// The waiting worker is only activated when the user chooses Refresh.
export const registerServiceWorker = (onUpdateReady) => {
  navigator.serviceWorker.addEventListener('controllerchange', reloadOnce);

  const bundle = document.querySelector('script[src*="/static/js/bundle"]');
  const build = bundle?.src?.split('/').pop();
  const workerUrl = build ? `/sw.js?build=${encodeURIComponent(build)}` : '/sw.js';
  return navigator.serviceWorker.register(workerUrl, { updateViaCache: 'none' }).then(
    (registration) => {
      // An update may already be parked from a previous visit.
      if (!registration) return null;
      if (registration.waiting && navigator.serviceWorker.controller) {
        markUpdateReady(registration, onUpdateReady);
      }
      if (registration.installing) {
        trackInstalling(registration.installing, registration, onUpdateReady);
      }
      registration.addEventListener('updatefound', () => {
        trackInstalling(registration.installing, registration, onUpdateReady);
      });
      return registration;
    },
    (err) => {
      console.log('ServiceWorker registration failed: ', err);
      return null;
    }
  );
};

const waitForInstallation = (registration) =>
  new Promise((resolve) => {
    const worker = registration.installing;
    if (!worker || worker.state === 'installed' || worker.state === 'redundant') {
      resolve();
      return;
    }

    const finish = () => {
      clearTimeout(timeout);
      worker.removeEventListener('statechange', onStateChange);
      resolve();
    };
    const onStateChange = () => {
      if (worker.state === 'installed' || worker.state === 'redundant') {
        // Allow the registration.waiting pointer to settle.
        Promise.resolve().then(finish);
      }
    };
    const timeout = setTimeout(finish, 8000);
    worker.addEventListener('statechange', onStateChange);
    onStateChange();
  });

// A version mismatch is not proof that the new worker is already waiting.
// Ask the browser to check for it, wait for installation, then activate it.
// When no update is waiting, a normal network-first reload remains the fallback.
export const skipWaitingAndReload = async (registration) => {
  if (reloading) return;
  const serviceWorker = typeof navigator !== 'undefined' && navigator.serviceWorker;
  if (!serviceWorker) {
    reloadOnce();
    return;
  }

  let target = registration || updateReadyRegistration;
  try {
    if (!target && serviceWorker.getRegistration) {
      target = await serviceWorker.getRegistration();
    }
    if (target && !target.waiting && typeof target.update === 'function') {
      await target.update();
      if (target.installing) await waitForInstallation(target);
    }
  } catch (error) {
    // The network may be unavailable. A normal reload is still preferable to
    // leaving the user with a broken or half-styled app.
  }

  if (target && target.waiting) {
    serviceWorker.addEventListener('controllerchange', reloadOnce, { once: true });
    target.waiting.postMessage({ type: 'SKIP_WAITING' });
    return;
  }
  reloadOnce();
};
